import { Link, Stack, useFocusEffect, useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import { ActivityIndicator, ScrollView } from 'react-native';

import { EventAttendees } from '@/components/events/event-attendees';
import { EventCheckInPanel } from '@/components/events/event-check-in-panel';
import { EventDetails } from '@/components/events/event-details';
import { EventReminderControls } from '@/components/events/event-reminder-controls';
import { EventRegistrationButton } from '@/components/events/event-registration-button';
import { CalendarActionBadges } from '@/components/integrations/calendar-action-badges';
import { useSession } from '@/components/session-provider';
import { useTenant } from '@/components/tenant-provider';
import { Button } from '@sixam/ui/button';
import { Text } from '@sixam/ui/text';
import { fetchEventById, type Event } from '@/lib/events';
import { fetchTenantBySlug, getMembershipRole, type Tenant } from '@/lib/tenant';

type PublicEventDetailFooterProps = Readonly<{
  canEditEvent: boolean;
  event: Event;
  isGoing: boolean;
  isMember: boolean;
  tenant: Tenant | null;
  tenantSlug: string;
  userId: string | null;
}>;

function PublicEventDetailFooter({
  canEditEvent,
  event,
  isGoing,
  isMember,
  tenant,
  tenantSlug,
  userId,
}: PublicEventDetailFooterProps) {
  // Check-in happens on the full-screen station, so the roster is refetched
  // when this screen comes back into focus rather than after an inline scan.
  const [checkInVersion, setCheckInVersion] = React.useState(0);
  useFocusEffect(
    React.useCallback(() => {
      setCheckInVersion((version) => version + 1);
    }, [])
  );

  return (
    <>
      {tenant && canEditEvent ? (
        <Link
          href={{
            pathname: '/admin/events/[id]',
            params: { id: event.id },
          }}
          asChild
        >
          <Button variant="secondary">
            <Text>Edit event</Text>
          </Button>
        </Link>
      ) : null}
      {tenant && isGoing ? (
        <>
          <CalendarActionBadges
            event={event}
            returnPath={`/${tenantSlug}/events/${event.id}`}
            timeZone={tenant.timezone}
          />
          {isMember ? (
            <EventReminderControls
              event={event}
              tenantSlug={tenant.slug}
              timeZone={tenant.timezone}
              userId={userId}
            />
          ) : null}
        </>
      ) : null}
      {tenant && isMember ? (
        <EventAttendees eventId={event.id} refreshKey={checkInVersion} />
      ) : null}
      {tenant ? <EventCheckInPanel eventId={event.id} userId={userId} /> : null}
    </>
  );
}

export default function PublicEventDetailScreen() {
  const { tenant: tenantSlug, id } = useLocalSearchParams<{ tenant: string; id: string }>();
  const { profile, session } = useSession();
  const { memberships } = useTenant();

  const [tenant, setTenant] = React.useState<Tenant | null>(null);
  const [event, setEvent] = React.useState<Event | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isGoing, setIsGoing] = React.useState(false);

  React.useEffect(() => {
    let active = true;

    async function load() {
      setIsLoading(true);
      try {
        const [resolvedTenant, data] = await Promise.all([
          fetchTenantBySlug(tenantSlug),
          fetchEventById(id),
        ]);
        if (active) {
          setTenant(resolvedTenant);
          setEvent(data);
          setError(null);
        }
      } catch (loadError: unknown) {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load event');
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    void load();

    return () => {
      active = false;
    };
  }, [id, tenantSlug]);

  const membershipRole = getMembershipRole(memberships, tenant?.id, session?.user.id);
  const isMember = Boolean(membershipRole) && event?.tenant_id === tenant?.id;
  const isClubAdmin = ['admin', 'owner'].includes(membershipRole ?? '');
  const canEditEvent = profile?.role === 'admin' || isClubAdmin;
  const handleGoingChange = React.useCallback((nextIsGoing: boolean) => {
    setIsGoing(nextIsGoing);
  }, []);

  return (
    <>
      <Stack.Screen options={{ headerShown: true, title: 'Event' }} />
      <Stack.Screen.BackButton displayMode="minimal">Home</Stack.Screen.BackButton>

      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        className="flex-1 bg-background"
        contentContainerClassName="gap-4 p-6"
      >
        {isLoading ? <ActivityIndicator /> : null}

        {error ? (
          <Text className="text-destructive" selectable>
            {error}
          </Text>
        ) : null}

        {!isLoading && !event ? <Text variant="muted">This event is not available.</Text> : null}

        {event ? (
          <EventDetails
            event={event}
            timeZone={tenant?.timezone}
            primaryAction={
              tenant ? (
                <EventRegistrationButton
                  key={event.id}
                  event={event}
                  onGoingChange={handleGoingChange}
                  returnPath={`/${tenantSlug}/events/${event.id}`}
                  tenantName={tenant.name}
                  tenantSlug={tenant.slug}
                  timeZone={tenant.timezone}
                  userId={session?.user.id ?? null}
                />
              ) : null
            }
            footer={
              <PublicEventDetailFooter
                canEditEvent={canEditEvent}
                event={event}
                isGoing={isGoing}
                isMember={isMember}
                tenant={tenant}
                tenantSlug={tenantSlug}
                userId={session?.user.id ?? null}
              />
            }
          />
        ) : null}
      </ScrollView>
    </>
  );
}
